/**
 * useMapGeocoding Hook
 * Turns the content ref in scope into positions for a map block's place names.
 *
 * The request body is the REF — `{ kind, sessionId, recordId }` — and never the
 * places themselves. The back end loads that record, extracts its map fences and
 * resolves only names a model already wrote there. That is the entire reason
 * `/api/geo/resolve` can be public: geocoding runs against a deployment-wide
 * 1-request-per-second budget whose ban lands on our egress IP, and a public
 * route accepting `places: string[]` would be an open proxy against it. The body
 * schema is a `z.strictObject`, so sending place text is a 400 by construction —
 * but the reason it must never be attempted lives here, at the caller.
 *
 * Every request goes through `useApi()`. A raw `fetch` would bypass the typed
 * request contract, the backend URL and client-version handling, auth resolution
 * and the public/private gate.
 *
 * One request per RECORD, not per block: the cache key is the ref, so four map
 * fences in one deliverable share a single in-flight query and a single result.
 */

'use client';

import { useMemo } from 'react';
import { ROUTE_CONTRACTS } from '@cabal/shared/api-client';
import { GeoResolveStatus } from '@cabal/shared/domain/geo/enums.js';
import type { GeoResolveResponse } from '@cabal/shared/domain/geo/types.js';
import { useApi } from '@/contexts/api';
import { geoKeys, useGeoContentRef } from '@/contexts/geo';
import { useRouteAuthEnabled } from '@/hooks/routing';
import { useQueryHook } from '@/lib/query/use-query-hook';
import { QUERY_STATUS, type QueryStatus } from '@/types/async-protocol';
import {
  MapResolutionPhase,
  NO_PLACE_RESOLUTIONS,
  type MapPlaceResolution,
  type MapPlaceResolutions,
} from './map-render-model';

export interface UseMapGeocodingInput {
  /** False for a block of literal coordinates, which needs no lookup. */
  hasPlaces: boolean;
}

export interface MapGeocodingResult {
  phase: MapResolutionPhase;
  placeResolutions: MapPlaceResolutions;
}

/** In flight is `Resolving`; a settled response is `Settled`; a failed request
 *  is `Failed`. There is no fourth query state — `Unavailable` is not one of
 *  these, it is the absence of a query. */
const PHASE_BY_QUERY_STATUS: Record<QueryStatus, MapResolutionPhase> = {
  [QUERY_STATUS.Loading]: MapResolutionPhase.Resolving,
  [QUERY_STATUS.Errored]: MapResolutionPhase.Failed,
  [QUERY_STATUS.Success]: MapResolutionPhase.Settled,
};

/** No ref in scope: this block has no channel through which to acquire
 *  positions, which is `Unavailable` — not a failed lookup. */
const NO_RESOLUTION_CHANNEL: MapGeocodingResult = {
  phase: MapResolutionPhase.Unavailable,
  placeResolutions: NO_PLACE_RESOLUTIONS,
};

/** Nothing to look up, so positions are already final. */
const NOTHING_TO_RESOLVE: MapGeocodingResult = {
  phase: MapResolutionPhase.Settled,
  placeResolutions: NO_PLACE_RESOLUTIONS,
};

/**
 * The response, keyed by the place text exactly as the pin wrote it — which is
 * what `GeoResolveResult.query` echoes back.
 *
 * Every result is mapped independently, so a record where two places resolved
 * and two did not yields two markers and two explained rows. A partial outcome
 * NEVER discards the pins that did resolve.
 *
 * An ambiguous verdict is deliberately position-less even though the vendor had
 * coordinates in hand: `GeocodedLocation.isAmbiguous` means those coordinates
 * must not be presented as the answer.
 */
function toPlaceResolutions(response: GeoResolveResponse | null): MapPlaceResolutions {
  if (!response) {
    return NO_PLACE_RESOLUTIONS;
  }

  const resolutions = new Map<string, MapPlaceResolution>();

  response.results.forEach((result) => {
    const isPositioned =
      result.status === GeoResolveStatus.Resolved &&
      result.location != null &&
      !result.location.isAmbiguous;

    resolutions.set(result.query, {
      status: result.status,
      position:
        isPositioned && result.location
          ? { lat: result.location.lat, lon: result.location.lon }
          : null,
      candidates: result.candidates,
      name: result.location?.name ?? null,
    });
  });

  return resolutions;
}

export function useMapGeocoding({ hasPlaces }: UseMapGeocodingInput): MapGeocodingResult {
  const api = useApi();
  const contentRef = useGeoContentRef();
  const isResolvable = hasPlaces && contentRef != null;
  const routeEnabled = useRouteAuthEnabled({
    route: ROUTE_CONTRACTS.geo.resolve,
    enabled: isResolvable,
  });

  const query = useQueryHook<GeoResolveResponse, Error>({
    queryKey: geoKeys.resolve(contentRef),
    queryFn: ({ signal }) => {
      if (!contentRef) {
        // Unreachable: the query is disabled without a ref. Throwing rather than
        // inventing a ref keeps the "never resolve without one" rule total.
        throw new Error('useMapGeocoding: no content ref in scope');
      }

      return api.geo.resolve({ body: contentRef, signal });
    },
    options: { enabled: routeEnabled && isResolvable },
  });

  // Built from whatever data is in hand, independent of status: a refetch that
  // fails must not take away the markers the previous response already earned.
  const queryData = query.data;
  const placeResolutions = useMemo(() => toPlaceResolutions(queryData), [queryData]);

  if (!hasPlaces) {
    return NOTHING_TO_RESOLVE;
  }

  if (!contentRef) {
    return NO_RESOLUTION_CHANNEL;
  }

  return { phase: PHASE_BY_QUERY_STATUS[query.status], placeResolutions };
}
